import { Button, EmptyState, Input, Table, useConfirm, useToast } from '@/components';
import { ApiClientError, type UserEmailRow } from '@uchat-team-management/shared';
import { createSignal } from 'solid-js';
import { useAddUserEmail, useDeleteUserEmail, useMyUserEmails } from './hooks';

// Personal settings card: additional emails used only to match you with external
// services (e.g. Firebase App Distribution testers) — never for signing in.
export const MyEmailsSection = () => {
  const emails = useMyUserEmails();
  const add = useAddUserEmail();
  const remove = useDeleteUserEmail();
  const toast = useToast();
  const confirmDialog = useConfirm();

  const [email, setEmail] = createSignal('');

  const onAdd = async (e: SubmitEvent) => {
    e.preventDefault();
    try {
      await add.mutateAsync(email());
      setEmail('');
      toast.success('Email added');
    } catch (err) {
      if (err instanceof ApiClientError) toast.error(err.error.message);
      else toast.error('Failed to add email');
    }
  };

  const onDelete = async (row: UserEmailRow) => {
    const ok = await confirmDialog({
      title: `Remove "${row.email}"?`,
      body: 'It will no longer be used to match you with App Distribution testers.',
      confirmLabel: 'Remove',
      danger: true,
    });
    if (!ok) return;
    try {
      await remove.mutateAsync(row.id);
      toast.success('Email removed');
    } catch {
      toast.error('Failed to remove email');
    }
  };

  return (
    <div class="flex flex-col gap-4">
      <p class="text-sm text-neutral-600 dark:text-neutral-400">
        Other emails you use elsewhere — e.g. the one you accepted the App Distribution invite with
        — so your test devices can be matched to you. Sign-in always uses your primary email.
      </p>

      <Table
        data={emails.data?.data ?? []}
        rowKey={(r) => r.id}
        loading={emails.isLoading}
        empty={<EmptyState title="No other emails" body="Add an email you use elsewhere below." />}
        columns={[
          {
            key: 'email',
            header: 'Email',
            render: (row: UserEmailRow) => <span class="font-medium">{row.email}</span>,
          },
          {
            key: 'actions',
            header: '',
            align: 'right',
            render: (row: UserEmailRow) => (
              <Button size="sm" variant="ghost" onClick={() => void onDelete(row)}>
                Remove
              </Button>
            ),
          },
        ]}
      />

      <form class="flex flex-wrap items-end gap-3" onSubmit={(e) => void onAdd(e)}>
        <div class="min-w-64 flex-1">
          <Input
            label="Email"
            type="email"
            value={email()}
            onInput={(e) => setEmail(e.currentTarget.value)}
            placeholder="you@gmail.com"
            required
          />
        </div>
        <Button type="submit" loading={add.isPending}>
          Add email
        </Button>
      </form>
    </div>
  );
};
